import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  DraftCondition,
  DraftGroup,
  andChild,
  countDraftConditions,
  draftAssociation,
  draftCondition,
  draftGroup,
  findDraftNode,
  isConditionComplete,
  isFlatDraft,
  removeDraftNode,
  updateDraft
} from '@core/filters/path-filter/draft';
import { defaultOperator } from '@core/filters/path-filter/vocabulary/default-operators';
import {
  FilterAssociation,
  FilterField,
  FilterVocabulary
} from '@core/filters/path-filter/vocabulary/vocabulary';
import { FieldPath } from '@core/filters/path-filter/wire';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';
import { foldForSearch } from '@core/utils/search-text.utils';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { AvailableLanguages } from '@shared/types/translation.type';

import { PathFilterConditionComponent } from '../condition/path-filter-condition.component';
import {
  FIELD_TYPE_ICONS,
  argumentSummary,
  associationLabel,
  closedValueLabel,
  conditionFieldLabel,
  fieldLabel,
  fieldTypeHint,
  operatorLabel
} from '../path-filter-labels';
import { injectPathFilterRegistries } from '../path-filter-registries';
import { ComposerContext } from '../path-filter.models';

interface Pill {
  id: string;
  field: string;
  op: string;
  value: string;
}

@Component({
  selector: 'app-path-filter-pills',
  imports: [
    TranslatePipe,
    DropdownComponent,
    FocusFirstDirective,
    PathFilterConditionComponent
  ],
  templateUrl: './path-filter-pills.component.html',
  styles: `
    :host {
      display: block;
    }
  `
})
export class PathFilterPillsComponent {
  private static instances = 0;

  draft = model.required<DraftGroup>();
  vocabulary = input.required<FilterVocabulary>();
  /** `pathKey` of the conditions the data layer rejected. */
  invalidPaths = input<readonly string[]>([]);

  openAdvanced = output<void>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly lang = computed(() => this.currentLang() as AvailableLanguages);
  private readonly injector = inject(Injector);
  private readonly addButton = viewChild.required<ElementRef<HTMLElement>>('addButton');

  protected readonly menuOpen = signal(false);
  protected readonly fieldSearch = signal('');
  /** Index into the field options followed by the association options; -1 for none. */
  protected readonly highlighted = signal(-1);
  protected readonly menuId = `path-filter-pills-${++PathFilterPillsComponent.instances}`;
  /** The condition whose value popover is open, and the element it hangs from. */
  private readonly editing = signal<{ id: string; anchor: HTMLElement } | null>(null);
  /** As a list so that moving to another pill recreates the popover at its new anchor. */
  protected readonly editingList = computed(() => {
    const editing = this.editing();
    return editing ? [editing] : [];
  });

  private readonly registries = injectPathFilterRegistries();
  protected readonly context = computed<ComposerContext>(() => ({
    vocabulary: this.vocabulary(),
    mode: 'data',
    parameters: [],
    selfLabel: null,
    invalidPaths: this.invalidPaths(),
    tags: this.registries.tags(),
    labels: this.registries.labels(),
    requestParameter: () => undefined
  }));
  protected readonly folded = computed(() => !isFlatDraft(this.draft()));
  protected readonly conditionCount = computed(() => countDraftConditions(this.draft()));

  /** A condition the panel added before its field was picked filters nothing and has no label. */
  protected readonly pills = computed<Pill[]>(() => {
    const lang = this.lang();
    if (this.folded()) return [];
    return this.draft()
      .children.filter(
        (child): child is DraftCondition & { path: FieldPath } =>
          child.kind === 'condition' && child.path !== null
      )
      .map((condition) => {
        const known = this.vocabulary().field(null, condition.path);
        const field = known ?? { label: null, path: condition.path };
        return {
          id: condition.id,
          field: conditionFieldLabel(field, condition.path, lang, this.translate),
          op: condition.op ? operatorLabel(condition.op, this.translate) : '',
          value: condition.op
            ? argumentSummary(condition.op, condition.arg, this.translate, (value) =>
                closedValueLabel(known, value, lang)
              )
            : ''
        };
      });
  });

  private readonly term = computed(() => foldForSearch(this.fieldSearch().trim()));

  protected readonly fieldOptions = computed(() => {
    const lang = this.lang();
    const term = this.term();
    return this.vocabulary()
      .fields(null)
      .map((field) => ({
        field,
        label: fieldLabel(field, lang, this.translate),
        icon: FIELD_TYPE_ICONS[field.type],
        hint: fieldTypeHint(field.type, this.translate)
      }))
      .filter(
        (option) =>
          term === '' ||
          foldForSearch(option.label).includes(term) ||
          option.field.key.includes(term)
      );
  });

  protected readonly associationOptions = computed(() => {
    const lang = this.lang();
    const term = this.term();
    return this.vocabulary()
      .associations()
      .map((association) => ({
        association,
        label: associationLabel(association, lang, this.translate)
      }))
      .filter((option) => term === '' || foldForSearch(option.label).includes(term));
  });

  protected readonly editedCondition = computed(() => {
    const editing = this.editing();
    const node = editing ? findDraftNode(this.draft(), editing.id) : undefined;
    return node?.kind === 'condition' ? node : null;
  });

  protected readonly closeMenu = () => {
    this.menuOpen.set(false);
    this.fieldSearch.set('');
    this.highlighted.set(-1);
  };

  protected optionId(index: number): string {
    return `${this.menuId}-option-${index}`;
  }

  /** Null when nothing is highlighted or the search left no item under the highlight. */
  protected readonly activeOptionId = computed(() => {
    const index = this.highlighted();
    const count = this.fieldOptions().length + this.associationOptions().length;
    return index >= 0 && index < count ? this.optionId(index) : null;
  });

  protected onFieldSearch(text: string): void {
    this.fieldSearch.set(text);
    this.highlighted.set(0);
  }

  protected onFieldSearchKey(event: KeyboardEvent): void {
    const fields = this.fieldOptions();
    const associations = this.associationOptions();
    const count = fields.length + associations.length;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (count === 0) return;
        this.highlighted.update((index) =>
          event.key === 'ArrowDown'
            ? (index + 1) % count
            : index <= 0
              ? count - 1
              : index - 1
        );
        afterNextRender(
          () =>
            document
              .getElementById(this.optionId(this.highlighted()))
              ?.scrollIntoView({ block: 'nearest' }),
          { injector: this.injector }
        );
        return;
      }
      case 'Enter': {
        event.preventDefault();
        const index = this.highlighted();
        if (index >= 0 && index < fields.length) this.pickField(fields[index].field);
        else if (index >= fields.length && index < count)
          this.pickAssociation(associations[index - fields.length].association);
        return;
      }
      // The overlay stops key events at its panels, so its own Escape listener never sees this one.
      case 'Escape':
        this.closeMenu();
        this.addButton().nativeElement.focus();
        return;
    }
  }

  protected readonly closeEditor = () => {
    const condition = this.editedCondition();
    this.editing.set(null);
    // A pill left without a value would read as a filter while filtering nothing.
    if (condition && !isConditionComplete(condition)) {
      this.draft.set(removeDraftNode(this.draft(), condition.id));
    }
  };

  // The overlay stops key events at its panels, so its own Escape listener never sees this one.
  protected onPopoverKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    const editing = this.editing();
    this.closeEditor();
    const kept = editing && findDraftNode(this.draft(), editing.id);
    const anchor = kept ? editing.anchor : this.addButton().nativeElement;
    // A pill anchors on its whole chip; its first button is the one that opened the popover.
    (anchor.matches('button') ? anchor : anchor.querySelector('button'))?.focus();
  }

  /** Adds a condition on `field` with its default operator and opens its value popover. */
  openField(field: FilterField): void {
    const condition = draftCondition(
      field.path,
      defaultOperator(field, this.vocabulary()),
      null
    );
    this.draft.set(andChild(this.draft(), condition));
    this.editing.set({ id: condition.id, anchor: this.addButton().nativeElement });
  }

  /**
   * The popover hangs from the same button as the closing field menu, and the dropdown singleton
   * hands back an entry still open on that target instead of opening a new one; waiting for the
   * menu's close to render first is what makes the popover appear.
   */
  protected pickField(field: FilterField): void {
    this.closeMenu();
    afterNextRender(() => this.openField(field), { injector: this.injector });
  }

  /** A related entity cannot be a pill, so picking one goes straight to the panel. */
  protected pickAssociation(association: FilterAssociation): void {
    this.closeMenu();
    const block = {
      ...draftAssociation(association.edge, association.quantifiers[0] ?? 'any'),
      filter: draftGroup('and', [draftCondition()])
    };
    this.draft.set(andChild(this.draft(), block));
    this.openAdvanced.emit();
  }

  protected edit(id: string, anchor: HTMLElement): void {
    this.editing.set({ id, anchor });
  }

  protected onEdited(condition: DraftCondition): void {
    this.draft.set(updateDraft(this.draft(), condition.id, () => condition));
  }

  protected remove(id: string): void {
    if (this.editing()?.id === id) this.editing.set(null);
    this.draft.set(removeDraftNode(this.draft(), id));
  }

  protected clear(): void {
    this.editing.set(null);
    this.draft.set(draftGroup());
  }
}
